import { useQuery } from "@apollo/client";
import { Controller, useFormContext } from "react-hook-form";
import { FormGroup } from "@cofano/cck";
import z from "zod";
import { CharterType, StatusType } from "__generated__/enums";
import { InvoiceCountDocument } from "__generated__/view/dashboard/graphql/dashboard.queries.generated";
import { GetAllStatusesDocument } from "__generated__/view/statuses/graphql/queries.generated";
import CharterTypeSelect from "view/charters/components/selects/CharterTypeSelect";
import StatusSelect from "view/statuses/components/selects/StatusSelect";
import { createWidgetSource } from "view/dashboard/widgets/sources/types";
import { intl } from "LocaleWrapper";

export default createWidgetSource({
	valueType: "number",
	label: intl.formatMessage({ id: "INVOICES" }),
	configSchema: z.object({
		statusIds: z.array(z.number()).default([]),
		charterType: z.enum(CharterType).nullable().default(null),
	}),

	form: ({ prefix }) => {
		const { control } = useFormContext();

		const { data: statusData } = useQuery(GetAllStatusesDocument, {
			variables: { search: { types: [ StatusType.Invoice ] } },
		});
		const statuses = statusData?.allStatuses ?? [];

		return <>
			<FormGroup label={intl.formatMessage({ id: "CHARTER_TYPE" })} labelPlacement="left">
				<Controller
					name={`${prefix}.charterType`}
					control={control}
					render={({ field }) => (
						<CharterTypeSelect
							value={(field.value as CharterType) ?? null}
							onChange={selected => field.onChange(selected ?? null)} />
					)} />
			</FormGroup>

			<FormGroup label={intl.formatMessage({ id: "STATUS" })} labelPlacement="left">
				<Controller
					name={`${prefix}.statusIds`}
					control={control}
					render={({ field }) => {
						const ids = (field.value as Array<number>) ?? [];

						return <StatusSelect
							multiple
							statuses={statuses}
							value={statuses.filter(status => ids.includes(status.id))}
							onChange={selected => field.onChange((selected ?? []).map(status => status.id))} />;
					}} />
			</FormGroup>
		</>;
	},

	useValue: config => {
		const { data, loading, error } = useQuery(InvoiceCountDocument, {
			variables: {
				search: {
					statuses: config.statusIds.length > 0 ? config.statusIds : undefined,
					charterType: config.charterType ?? undefined,
				},
				pagination: { page: 0, size: 1 },
			},
		});

		return { value: data?.searchInvoices.totalElements, loading, error };
	},
});
